import React, { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Loader2, Upload, FileText, Image, X, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DynamicFormRenderer = ({ 
  fields, 
  values, 
  onChange, 
  language = 'es',
  errors = {},
  targetStages = []
}) => {
  const [uploadingFields, setUploadingFields] = useState({});

  const t = {
    es: {
      required: 'Obligatorio',
      uploading: 'Subiendo...',
      uploadSuccess: 'Archivo subido',
      uploadError: 'Error al subir',
      removeFile: 'Eliminar archivo',
      dragOrClick: 'Arrastra o haz clic para subir',
      maxSize: 'Máx. 20MB'
    },
    en: {
      required: 'Required',
      uploading: 'Uploading...',
      uploadSuccess: 'File uploaded',
      uploadError: 'Upload error',
      removeFile: 'Remove file',
      dragOrClick: 'Drag or click to upload',
      maxSize: 'Max. 20MB'
    }
  }[language];

  const getLocalizedValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      return value[language] || value.es || value.en || '';
    }
    return value || '';
  };

  const handleFileUpload = async (fieldId, file, fieldType) => {
    if (!file) return;

    setUploadingFields(prev => ({ ...prev, [fieldId]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = fieldType === 'image' 
        ? `${API_URL}/uploads/program-image` 
        : `${API_URL}/uploads/document`;

      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = response.data.url || response.data.primary_url;
      
      onChange(fieldId, {
        url: fileUrl,
        name: file.name,
        type: file.type
      });
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingFields(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const handleRemoveFile = (fieldId) => {
    onChange(fieldId, null);
  };

  const sortedFields = [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));

  const renderField = (field) => {
    const value = values[field.id];
    const label = getLocalizedValue(field.label);
    const placeholder = getLocalizedValue(field.placeholder);
    const helpText = getLocalizedValue(field.helpText);
    const isUploading = uploadingFields[field.id];
    const error = errors[field.id];

    switch (field.type) {
      case 'text_short':
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>
              {label} {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id={field.id}
              name={field.id}
              value={value || ''}
              onChange={(e) => onChange(field.id, e.target.value)}
              placeholder={placeholder}
              className={`border-2 ${error ? 'border-red-300' : 'border-gray-200'}`}
              required={field.required}
            />
            {helpText && <p className="text-sm text-gray-500">{helpText}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'text_long':
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>
              {label} {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Textarea
              id={field.id}
              name={field.id}
              value={value || ''}
              onChange={(e) => onChange(field.id, e.target.value)}
              placeholder={placeholder}
              className={`border-2 min-h-32 ${error ? 'border-red-300' : 'border-gray-200'}`}
              required={field.required}
            />
            {helpText && <p className="text-sm text-gray-500">{helpText}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'select': {
        // Filter stage options based on targetStages if this is the stage field
        let filteredOptions = field.options || [];
        if (field.id === 'stage' && targetStages && targetStages.length > 0) {
          const stageMapping = {
            'Idea': 'idea',
            'Pre-Seed': 'idea',
            'Seed': 'prototype',
            'MVP': 'mvp',
            'Early Stage': 'early-revenue',
            'Growth': 'growth',
            'Scale': 'growth'
          };
          const allowedValues = new Set(targetStages.map(s => stageMapping[s]).filter(Boolean));
          filteredOptions = filteredOptions.filter(opt => allowedValues.has(opt.value));
        }
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>
              {label} {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Select
              value={value || ''}
              onValueChange={(val) => onChange(field.id, val)}
              required={field.required}
            >
              <SelectTrigger className={`border-2 ${error ? 'border-red-300' : 'border-gray-200'}`}>
                <SelectValue placeholder={placeholder || label} />
              </SelectTrigger>
              <SelectContent>
                {filteredOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {getLocalizedValue(option.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {helpText && <p className="text-sm text-gray-500">{helpText}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );
      }

      case 'checkbox':
        return (
          <div key={field.id} className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id={field.id}
                checked={value || false}
                onCheckedChange={(checked) => onChange(field.id, checked)}
                required={field.required}
              />
              <Label htmlFor={field.id} className="cursor-pointer">
                {label} {field.required && <span className="text-red-500">*</span>}
              </Label>
            </div>
            {helpText && <p className="text-sm text-gray-500 ml-7">{helpText}</p>}
            {error && <p className="text-sm text-red-500 ml-7">{error}</p>}
          </div>
        );

      case 'document':
      case 'image':
        const IconComponent = field.type === 'image' ? Image : FileText;
        const acceptTypes = field.acceptedFileTypes?.join(',') || 
          (field.type === 'image' ? '.jpg,.jpeg,.png,.gif,.webp' : '.pdf,.doc,.docx,.ppt,.pptx');
        
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>
              {label} {field.required && <span className="text-red-500">*</span>}
            </Label>
            
            {value?.url ? (
              <div className="flex items-center gap-3 p-4 border-2 border-green-200 bg-green-50 rounded-lg">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">{value.name}</p>
                  <p className="text-sm text-green-600">
                    {t.uploadSuccess}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemoveFile(field.id)}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t.removeFile}
                </Button>
              </div>
            ) : (
              <div className="relative">
                <div className={`
                  border-2 border-dashed rounded-lg p-6 text-center transition-colors
                  ${isUploading ? 'border-purple-300 bg-purple-50' : 'border-gray-300 hover:border-purple-400'}
                `}>
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                      <span className="text-purple-600">{t.uploading}</span>
                    </div>
                  ) : (
                    <>
                      <IconComponent className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">{t.dragOrClick}</p>
                      <p className="text-sm text-gray-400">{t.maxSize}</p>
                    </>
                  )}
                  <input
                    type="file"
                    id={field.id}
                    accept={acceptTypes}
                    onChange={(e) => handleFileUpload(field.id, e.target.files[0], field.type)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isUploading}
                  />
                </div>
              </div>
            )}
            {helpText && <p className="text-sm text-gray-500">{helpText}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {sortedFields.map(renderField)}
    </div>
  );
};

export default DynamicFormRenderer;
